/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, forwardRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import parsePhoneNumber, { E164Number } from 'libphonenumber-js';
import ReactPhoneInput from 'react-phone-number-input/input';

import Input, { InputProps, InputType } from 'ui/Input';

import { PhoneInputProps } from './types';

import styles from './index.module.scss';

type InputWrapperProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  inputProps?: InputProps;
};

const InputWrapper = forwardRef<HTMLInputElement, InputWrapperProps>((props: InputWrapperProps, ref) => {
  const { value, size, placeholder, autoComplete, inputProps, onChange, ...rest } = props;

  return (
    <Input
      {...rest}
      ref={ref}
      onChange={(_value, event) => onChange?.(event as React.ChangeEvent<HTMLInputElement>)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      value={value as string}
      elementId={inputProps?.elementId || ''}
      label={inputProps?.label}
      type={inputProps?.type || InputType.TEL}
      error={inputProps?.error}
      required={inputProps?.required}
      isDisabled={inputProps?.isDisabled}
      isLoading={inputProps?.isLoading}
      inputClassName={inputProps?.inputClassName}
      containerClassName={inputProps?.containerClassName}
      adornmentLeft={inputProps?.adornmentLeft}
      adornmentRight={inputProps?.adornmentRight}
      inputRef={inputProps?.inputRef}
      onEnterPressed={inputProps?.onEnterPressed}
      onMouseOver={inputProps?.onMouseOver}
      onMouseOut={inputProps?.onMouseOut}
    />
  );
});

const PhoneInput: FC<PhoneInputProps> = (props) => {
  const { initialValue, className, placeholder, onChange, onBlur, onFocus, onMouseOver, onMouseOut } = props;

  const [value, setValue] = useState<string | undefined>(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlurWrapper = (event: React.FocusEvent<HTMLInputElement>) => {
    const parsedValue = parsePhoneNumber(event.target.value);
    const formattedValue = parsedValue?.formatInternational();

    if (formattedValue) {
      onChange(formattedValue as E164Number);
    }

    onBlur?.(event);
  };

  const handleChange = (value?: E164Number) => {
    if (!value) {
      onChange('' as E164Number);
      return;
    }

    let processedValue = value;

    if (value.startsWith('+8')) {
      processedValue = `+7${value.slice(2)}` as E164Number;
    }

    // Если ввели "+" и потом что-то кроме цифр → оставляем "+"
    else if (value.startsWith('+') && value.length > 1 && !/^\+[0-9]+$/.test(value)) {
      processedValue = value;
    }

    // Если номер начинается с "8" (но не "+8") → преобразуем в "+7"
    else if (value.startsWith('8') && !value.startsWith('+')) {
      processedValue = `+7${value.slice(1)}` as E164Number;
    }

    // Если номер начинается с цифры (но не "8") → добавляем "+"
    else if (/^[0-9]/.test(value) && !value.startsWith('+')) {
      processedValue = `+${value}` as E164Number;
    }

    // Во всех остальных случаях оставляем как есть
    else {
      processedValue = value;
    }

    setValue(processedValue);
    onChange(processedValue);
  };

  return (
    <div className={classNames(styles.PhoneInput, className)}>
      <ReactPhoneInput
        international
        value={value}
        placeholder={placeholder}
        inputComponent={InputWrapper}
        inputProps={props}
        onChange={handleChange}
        onBlur={onBlurWrapper}
        onFocus={onFocus}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
      />
    </div>
  );
};

export default React.memo(PhoneInput);
